import { animate, style, transition, trigger } from "@angular/animations";
import { DatePipe } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { Component, EventEmitter, inject, LOCALE_ID, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from "primeng/fileupload";
import { FileUploadErrorEvent } from "primeng/fileupload/fileupload.interface";
import { TieredMenuModule } from "primeng/tieredmenu";
import { TooltipModule } from "primeng/tooltip";
import { Subject, takeUntil } from "rxjs";
import {
  FileContentListComponent,
  FileInfo,
} from "src/app/common/components/file-content-list/file-content-list.component";
import { DragAndDropItem } from "src/app/common/models/drag-and-drop-item.model";
import { SearchResult } from "src/app/common/models/search-result.model";
import { SearchTypeToLabelPipe } from "src/app/common/pipes/search-type-to-label-pipe";
import { AclService } from "src/app/common/services/acl.service";
import { FileContentService } from "src/app/common/services/file-content.service";
import { GoogleCloudService } from "src/app/common/services/google-cloud.service";
import { NotificationService } from "src/app/common/services/notification.service";
import { SearchService } from "src/app/common/services/search.service";
import { UserTrackingService } from "src/app/common/services/user-tracking.service";
import { mediaToString } from "src/app/common/tools/textUtils";
import { fileContentExtensions } from "src/app/config";
import { deserializeToAudiovisualMedia } from "src/app/modules/audiovisual-media/models/audiovisual-media.model";
import { deserializeToInstagramPost } from "src/app/modules/instagram/models/instagram-post.model";
import { deserializeToSpeech2Text } from "src/app/modules/speech2text/models/speech2text.model";

@Component({
  selector: "app-add-context-menu",
  standalone: true,
  imports: [
    ButtonModule,
    FileUploadModule,
    TooltipModule,
    TieredMenuModule,
    AutoCompleteModule,
    SearchTypeToLabelPipe,
    DatePipe,
  ],
  templateUrl: "./add-context-menu.component.html",
  styleUrl: "./add-context-menu.component.scss",
  animations: [
    trigger("fadeScale", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate("300ms ease-out", style({ opacity: 1, transform: "scale(1)" })),
      ]),
      transition(":leave", [animate("0ms ease-in", style({ opacity: 0, transform: "scale(0.95)" }))]),
    ]),
  ],
})
export class AddContextMenuComponent implements OnDestroy, OnInit {
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly fileContentService = inject(FileContentService);
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly googleCloudService = inject(GoogleCloudService);
  private readonly searchService = inject(SearchService);
  private readonly msalService = inject(MsalService);
  private readonly aclService = inject(AclService);
  private readonly destroySubject = new Subject<void>();

  locale = inject(LOCALE_ID);
  language = this.locale.toUpperCase();

  addContextHovered = false;
  mouseLeaveTimeout: NodeJS.Timeout | undefined;
  canSearchContents = false;
  searchResults: SearchResult[] = [];
  searchInProgress = false;
  selectedContent: DragAndDropItem | null = null;

  protected readonly validExtensions = fileContentExtensions;
  MAX_FILE_SIZE = 20 * 1024 * 1024; //20MB

  @ViewChild("fileUpload", { static: false }) fileUpload!: FileUpload;
  @ViewChild("autoComplete", { static: false }) autoComplete!: AutoComplete;
  @Output() readonly filesSelected = new EventEmitter<FileInfo[]>();
  @Output() readonly contentSelected = new EventEmitter<DragAndDropItem>();

  uploadedFilesListRef: DynamicDialogRef | undefined;
  username!: string;

  async ngOnInit(): Promise<void> {
    this.username = this.msalService.instance.getAllAccounts()[0]!.name!;
    this.canSearchContents = await this.aclService.isAuthorized("BETA_TESTER");
  }

  ngOnDestroy(): void {
    this.clearTimeout();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  selectExistingFile(): void {
    this.uploadedFilesListRef = this.dialogService.open(FileContentListComponent, {
      header: $localize`:@@prompt_add_file_to_prompt:Ajouter des fichiers au prompt`,
      width: "70%",
      data: {
        selectionMode: true,
      },
    });
    this.uploadedFilesListRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((selectedFiles: FileInfo[]) => {
      if (selectedFiles) {
        selectedFiles = selectedFiles.map((file) => ({
          id: file.id,
          filename: file.filename,
          filepath: file.filepath,
        }));
        this.filesSelected.emit(selectedFiles);
      }
    });
  }

  async uploadHandler(event: FileUploadHandlerEvent): Promise<void> {
    try {
      this.notificationService.showInfo("Fichiers en cours de transfert");
      // Upload to AWS S3 (existing logic)
      const responsePerFile = await this.fileContentService.upload(this.username, event.files);

      // Check S3 response, optionally handle partial failures
      for (const res of responsePerFile) {
        if (res.status !== "success") {
          const errorMessage = `Erreur d'upload du fichier: ${res.filename}  ${res.detail}`;
          this.notificationService.showWarning(errorMessage);
        }
      }

      // Upload the SAME files to Google Cloud
      for (const res of responsePerFile) {
        if (res.filepath) {
          try {
            // Call the new backend endpoint
            await this.googleCloudService.uploadFileFromS3(res.filepath);
          } catch (gcError) {
            console.error("Error uploading to Google Cloud:", res.filepath, gcError);
            this.notificationService.showWarning(`Erreur d'upload GCloud: ${res.filepath} => ${String(gcError)}`);
          }
        }
      }

      // Add the uploaded files to the list
      const uploadedFiles = responsePerFile.map((file) => ({
        id: file.filepath.replace(/\.[^/.]+$/, ""), // Regex to remove extansion
        filename: file.filename,
        filepath: file.filepath,
      }));
      this.filesSelected.emit(uploadedFiles);
      this.notificationService.showInfo("Fichiers ajoutés");
    } catch (e) {
      this.notificationService.showError(e, "Erreur lors de l'upload");
    } finally {
      this.fileUpload.clear();
      // Track user action
      await this.userTrackingService.track(this.username, "PROMPT_UPLOAD_FILE");
    }
  }

  onError(event: FileUploadErrorEvent): void {
    console.log("File upload error: ", event.error?.message);
  }

  protected async search(inTerm: string): Promise<void> {
    this.searchInProgress = true;
    const term = inTerm.trim().toLowerCase();
    let params = new HttpParams();
    params = params.set("text", term);
    const result = await this.searchService.search(params);
    if (result) {
      // sort result by date DESC
      result.sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
      this.searchResults = result;
    } else {
      this.searchResults = [];
    }
    this.searchInProgress = false;
  }

  async selectSearchResult(searchResult?: SearchResult): Promise<void> {
    if (!searchResult) {
      return;
    }
    this.autoComplete.disabled = true;
    this.selectedContent = null;
    if (searchResult.type === "ARTICLE") {
      const data = await this.searchService.getArticle(searchResult);
      this.selectedContent = {
        id: searchResult.id,
        text: mediaToString(data, "ARTICLE", this.language),
        title: searchResult.title,
      };
    } else if (searchResult.type === "SPEECH2TEXT_AUDIO" || searchResult.type === "SPEECH2TEXT_VIDEO") {
      const data = deserializeToSpeech2Text(await this.searchService.getSpeech2text(searchResult));
      this.selectedContent = {
        id: searchResult.id,
        text: mediaToString(data, "SPEECH2TEXT", this.language),
        title: searchResult.title,
      };
    } else if (searchResult.type === "AUDIOVISUAL_MEDIA_AUDIO" || searchResult.type === "AUDIOVISUAL_MEDIA_VIDEO") {
      const data = deserializeToAudiovisualMedia(await this.searchService.getAudiovisualMedia(searchResult));
      this.selectedContent = {
        id: searchResult.id,
        text: mediaToString(data, "AUDIOVISUAL_MEDIA", this.language),
        title: searchResult.title,
      };
    } else if (searchResult.type === "AGENCY_NEWS") {
      const data = await this.searchService.getAgencyNews(searchResult);
      this.selectedContent = {
        id: searchResult.id,
        text: mediaToString(data, "AGENCY_NEWS", this.language),
        title: searchResult.title,
      };
    } else if (searchResult.type === "INSTAGRAM_POST") {
      const data = deserializeToInstagramPost(await this.searchService.getInstagram(searchResult));
      this.selectedContent = {
        id: searchResult.id,
        text: mediaToString(data, "INSTAGRAM_POST", this.language),
        title: searchResult.title,
      };
    }
    if (this.selectedContent) {
      this.contentSelected.emit(this.selectedContent);
      this.selectedContent = null;
      this.searchResults = [];
      this.autoComplete.clear();
    }
    this.autoComplete.disabled = false;
  }

  onMouseEnter(): void {
    this.addContextHovered = true;
    this.clearTimeout();
  }

  onMouseLeave(): void {
    this.clearTimeout();

    if (this.searchInProgress) {
      this.mouseLeaveTimeout = setTimeout(() => {
        if (this.searchInProgress) {
          this.onMouseLeave();
        } else {
          this.addContextHovered = false;
        }
      }, 2000);
    } else {
      this.addContextHovered = false;
    }
  }

  clearTimeout(): void {
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
      this.mouseLeaveTimeout = undefined;
    }
  }
}
